import { withStyles } from '@material-ui/core';

import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import { AuthFileTemplateEditor, AuthFileTemplateSelector, CLIENT_CUSTOM_FIELDS_STATUS, CreateImportTemplateButton, PENDING_AUTH_IMPORT_TEMPLATE_TABS, SelectedPendingAuthImportTemplate, getPendingAuthImportResultErrors, getPendingAuthImportTemplateId, getPendingAuthImportTemplateList } from './importAuthFiles.components';
import { GET_PENDING_AUTH_IMPORT_CLIENT_CUSTOM_FIELDS_FAILURE, GET_PENDING_AUTH_IMPORT_TEMPLATES_FAILURE, GET_PENDING_AUTH_IMPORT_TEMPLATE_FAILURE, POST_PENDING_AUTH_IMPORT_TEMPLATE_DRYRUN_FAILURE, POST_PENDING_AUTH_IMPORT_TEMPLATE_FAILURE, POST_PENDING_AUTH_IMPORT_TEMPLATE_SUBMIT_FAILURE, PUT_PENDING_AUTH_IMPORT_TEMPLATE_FAILURE } from './importAuthFiles.types';
import { Grid, Paper, Stack } from '@mui/material';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { getAllCountries, getAllEnumerations, getAllProvinces } from 'modules/enumerations/store/enumerationActions';
import { getClients } from 'modules/clients/clients.actions';
import { getPendingAuthImportClientCustomFields, getPendingAuthImportTemplate, getPendingAuthImportTemplates, postPendingAuthImportTemplate, putPendingAuthImportTemplate, submitPendingAuthImportTemplateFile, uploadPendingAuthImportTemplateFile } from './importAuthFiles.actions';
import { showToast } from 'modules/layout/layout.actions';

const styles = theme => ({
  root: {
    margin: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(1.5),
  },
});

function ImportAuthFilesView(props) {
  const {
    classes,
    clients,
    enumerations,
    getAllCountries: loadAllCountries,
    getAllEnumerations: loadAllEnumerations,
    getAllProvinces: loadAllProvinces,
    getClients: loadClients,
    getPendingAuthImportClientCustomFields: loadPendingAuthImportClientCustomFields,
    getPendingAuthImportTemplate: loadPendingAuthImportTemplate,
    getPendingAuthImportTemplates: loadPendingAuthImportTemplates,
    postPendingAuthImportTemplate: createPendingAuthImportTemplate,
    putPendingAuthImportTemplate: updatePendingAuthImportTemplate,
    showToast: showToastMessage,
    submitPendingAuthImportTemplateFile: submitPendingAuthImportTemplateFileForImport,
    uploadPendingAuthImportTemplateFile: dryRunPendingAuthImportTemplateFile,
  } = props;

  const [pendingAuthImportTemplates, setPendingAuthImportTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [selectedTemplateFile, setSelectedTemplateFile] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isDryRunLoading, setIsDryRunLoading] = useState(false);
  const [isImportSubmitting, setIsImportSubmitting] = useState(false);
  const [dryRunResults, setDryRunResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [selectedTemplateTab, setSelectedTemplateTab] = useState(PENDING_AUTH_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [selectedTemplateClientCustomFields, setSelectedTemplateClientCustomFields] = useState([]);
  const [selectedTemplateClientCustomFieldsStatus, setSelectedTemplateClientCustomFieldsStatus] = useState(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);

  // Resolves to the client's custom fields (standard and custom), or null when they could not be loaded
  const loadClientCustomFields = useCallback(async clientRecordId => {
    const response = await loadPendingAuthImportClientCustomFields(clientRecordId);

    if (response.type === GET_PENDING_AUTH_IMPORT_CLIENT_CUSTOM_FIELDS_FAILURE) {
      showToastMessage('Failed to load the client\'s custom fields. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return null;
    }

    // The API returns no content when the client has no fields
    return Array.isArray(response.response) ? response.response : [];
  }, [loadPendingAuthImportClientCustomFields, showToastMessage]);

  const selectedTemplateClientRecordId = selectedTemplate?.clientRecordId;

  const fetchPendingAuthImportTemplates = useCallback(async () => {
    setIsLoadingTemplates(true);

    const response = await loadPendingAuthImportTemplates();

    setIsLoadingTemplates(false);

    if (response.type === GET_PENDING_AUTH_IMPORT_TEMPLATES_FAILURE) {
      showToastMessage('Failed to load pending auth import templates. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setPendingAuthImportTemplates(getPendingAuthImportTemplateList(response.response));
  }, [loadPendingAuthImportTemplates, showToastMessage]);

  const handleTemplateSelected = useCallback(async templateId => {
    setSelectedTemplateId(templateId);
    setSelectedTemplate(null);
    setEditorTemplate(null);
    setSelectedTemplateFile(null);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateTab(PENDING_AUTH_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);

    if (!templateId) return;

    setIsLoadingTemplate(true);

    const response = await loadPendingAuthImportTemplate(templateId);

    setIsLoadingTemplate(false);

    if (response.type === GET_PENDING_AUTH_IMPORT_TEMPLATE_FAILURE) {
      showToastMessage('Failed to load pending auth import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setSelectedTemplate(response.response);
  }, [loadPendingAuthImportTemplate, showToastMessage]);

  const handleFileSelected = file => {
    setSelectedTemplateFile(file);
    setDryRunResults(null);
    setImportResults(null);
    setSelectedTemplateTab(PENDING_AUTH_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS);
  };

  const openTemplateEditor = () => {
    setEditorTemplate(selectedTemplate || null);
    setTemplateEditorOpen(true);
  };

  const handleTemplateSaved = async payload => {
    const templateId = getPendingAuthImportTemplateId(payload);
    const response = editorTemplate
      ? await updatePendingAuthImportTemplate(templateId, payload)
      : await createPendingAuthImportTemplate(payload);
    const failureType = editorTemplate ? PUT_PENDING_AUTH_IMPORT_TEMPLATE_FAILURE : POST_PENDING_AUTH_IMPORT_TEMPLATE_FAILURE;

    if (response.type === failureType) {
      showToastMessage('Failed to save pending auth import template. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return false;
    }

    showToastMessage('Pending auth import template saved successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
    await fetchPendingAuthImportTemplates();

    const savedTemplateId = getPendingAuthImportTemplateId(response.response);
    if (savedTemplateId) {
      setSelectedTemplateId(String(savedTemplateId));
      setSelectedTemplate(response.response);
    }

    setEditorTemplate(null);
    setTemplateEditorOpen(false);
    return true;
  };

  const handleDryRun = async () => {
    const templateId = selectedTemplateId || getPendingAuthImportTemplateId(selectedTemplate);

    if (!templateId || !selectedTemplateFile || isDryRunLoading || isImportSubmitting) return;

    setIsDryRunLoading(true);
    setImportResults(null);

    const response = await dryRunPendingAuthImportTemplateFile(templateId, selectedTemplateFile);

    setIsDryRunLoading(false);

    if (response.type === POST_PENDING_AUTH_IMPORT_TEMPLATE_DRYRUN_FAILURE) {
      setDryRunResults({ errors: response.messages ?? ['Failed to upload pending auth import file. Please try again.'] });
      setSelectedTemplateTab(PENDING_AUTH_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);
      showToastMessage('Pending auth dry run failed. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setDryRunResults(response.response);
    setSelectedTemplateTab(PENDING_AUTH_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS);

    if (getPendingAuthImportResultErrors(response.response).length > 0) {
      showToastMessage('Pending auth dry run completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('Pending auth dry run completed successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  const handleSubmitImport = async () => {
    const templateId = selectedTemplateId || getPendingAuthImportTemplateId(selectedTemplate);

    if (
      !templateId
      || !selectedTemplateFile
      || !dryRunResults
      || getPendingAuthImportResultErrors(dryRunResults).length > 0
      || isDryRunLoading
      || isImportSubmitting
    ) return;

    setIsImportSubmitting(true);

    const response = await submitPendingAuthImportTemplateFileForImport(templateId, selectedTemplateFile);

    setIsImportSubmitting(false);

    if (response.type === POST_PENDING_AUTH_IMPORT_TEMPLATE_SUBMIT_FAILURE) {
      setImportResults({ errors: response.messages ?? ['Failed to submit pending auth import file. Please try again.'] });
      setSelectedTemplateTab(PENDING_AUTH_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);
      showToastMessage('Pending auth submit failed. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    setImportResults(response.response);
    setSelectedTemplateTab(PENDING_AUTH_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS);

    if (getPendingAuthImportResultErrors(response.response).length > 0) {
      showToastMessage('Pending auth import completed with errors.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }

    showToastMessage('Pending auth import submitted successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
  };

  // The template view needs the client's fields to label custom field mappings and flag ones that are no longer available
  useEffect(() => {
    if (!selectedTemplateClientRecordId) {
      setSelectedTemplateClientCustomFields([]);
      setSelectedTemplateClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);
      return undefined;
    }

    let isCurrentClient = true;
    setSelectedTemplateClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.LOADING);

    loadClientCustomFields(selectedTemplateClientRecordId).then(customFields => {
      if (!isCurrentClient) return;

      setSelectedTemplateClientCustomFields(customFields ?? []);
      setSelectedTemplateClientCustomFieldsStatus(customFields ? CLIENT_CUSTOM_FIELDS_STATUS.LOADED : CLIENT_CUSTOM_FIELDS_STATUS.ERROR);
    });

    return () => {
      isCurrentClient = false;
    };
  }, [loadClientCustomFields, selectedTemplate, selectedTemplateClientRecordId]);

  useEffect(() => {
    fetchPendingAuthImportTemplates();
  }, [fetchPendingAuthImportTemplates]);

  useEffect(() => {
    if (!enumerations) {
      loadAllEnumerations();
      return;
    }

    if (!enumerations?.countries) {
      loadAllCountries();
    }

    if (!enumerations?.provinces) {
      loadAllProvinces();
    }
  }, [enumerations, loadAllCountries, loadAllEnumerations, loadAllProvinces]);

  useEffect(() => {
    if (!Array.isArray(clients) || clients.length === 0) {
      loadClients();
    }
  }, [clients, loadClients]);

  return (
    <div className={`${classes.root} p-2`}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <AuthFileTemplateSelector
                  isLoading={isLoadingTemplates}
                  templates={pendingAuthImportTemplates}
                  selectedTemplateId={selectedTemplateId}
                  onTemplateSelected={handleTemplateSelected}
                />
              </Stack>
              <CreateImportTemplateButton isEditMode={Boolean(selectedTemplate)} disabled={isLoadingTemplate} onClick={openTemplateEditor} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {(selectedTemplateId || isLoadingTemplate) && (
            <SelectedPendingAuthImportTemplate
              activeTab={selectedTemplateTab}
              clientCustomFields={selectedTemplateClientCustomFields}
              clientCustomFieldsStatus={selectedTemplateClientCustomFieldsStatus}
              clients={clients}
              dryRunResults={dryRunResults}
              enumerations={enumerations}
              importResults={importResults}
              isDryRunLoading={isDryRunLoading}
              isImportSubmitting={isImportSubmitting}
              isLoading={isLoadingTemplate}
              selectedTemplateFile={selectedTemplateFile}
              template={selectedTemplate}
              onDryRun={handleDryRun}
              onFileSelected={handleFileSelected}
              onSubmitImport={handleSubmitImport}
              onTabChange={setSelectedTemplateTab}
            />
          )}
        </Grid>
      </Grid>
      <AuthFileTemplateEditor
        clients={clients}
        enumerations={enumerations}
        initialTemplate={editorTemplate}
        isEditMode={Boolean(editorTemplate)}
        open={templateEditorOpen}
        onClose={() => setTemplateEditorOpen(false)}
        onLoadClientCustomFields={loadClientCustomFields}
        onSaveTemplate={handleTemplateSaved}
      />
    </div>
  );
}

ImportAuthFilesView.propTypes = {
  classes: PropTypes.object.isRequired,
  clients: PropTypes.arrayOf(PropTypes.object),
  enumerations: PropTypes.object,
  getAllCountries: PropTypes.func.isRequired,
  getAllEnumerations: PropTypes.func.isRequired,
  getAllProvinces: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getPendingAuthImportClientCustomFields: PropTypes.func.isRequired,
  getPendingAuthImportTemplate: PropTypes.func.isRequired,
  getPendingAuthImportTemplates: PropTypes.func.isRequired,
  postPendingAuthImportTemplate: PropTypes.func.isRequired,
  putPendingAuthImportTemplate: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  submitPendingAuthImportTemplateFile: PropTypes.func.isRequired,
  uploadPendingAuthImportTemplateFile: PropTypes.func.isRequired,
};

ImportAuthFilesView.defaultProps = {
  clients: [],
  enumerations: null,
};

const mapStateToProps = state => ({
  clients: state.clients?.unmappedClients || [],
  enumerations: state.enumerations ? state.enumerations.allEnums : null,
});

export default compose(
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, {
    getAllCountries,
    getAllEnumerations,
    getAllProvinces,
    getClients,
    getPendingAuthImportClientCustomFields,
    getPendingAuthImportTemplate,
    getPendingAuthImportTemplates,
    postPendingAuthImportTemplate,
    putPendingAuthImportTemplate,
    showToast,
    submitPendingAuthImportTemplateFile,
    uploadPendingAuthImportTemplateFile,
  }),
)(ImportAuthFilesView);
